import React, { useState, useMemo } from 'react';
import { Incident } from '../types/incident';
import { initialCaseTypes } from '../data/caseTypes';
import { states, getStateName } from '../data/geography';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { FileText, Map, Activity, AlertTriangle, Calendar, Download, Filter } from 'lucide-react';
interface ReportsAnalyticsProps {
  incidents: Incident[];
}
type ReportTab = 'National' | 'Geographic' | 'IncidentType' | 'Casualty' | 'Periodic';
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#ffc658', '#ef5350', '#82ca9d'];
export function ReportsAnalytics({
  incidents
}: ReportsAnalyticsProps) {
  const [activeTab, setActiveTab] = useState<ReportTab>('National');

  // Filter State
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [selectedType, setSelectedType] = useState('');

  // Apply Filters (Only consider approved incidents for official reports)
  const filteredIncidents = useMemo(() => {
    return incidents.filter(i => {
      if (i.status !== 'Approved') return false;
      if (startDate && i.date < startDate) return false;
      if (endDate && i.date > endDate) return false;
      if (selectedState && i.stateId !== selectedState) return false;
      if (selectedType && i.caseTypeId !== selectedType) return false;
      return true;
    });
  }, [incidents, startDate, endDate, selectedState, selectedType]);

  // --- Data Aggregations ---

  // National Summary
  const totalIncidents = filteredIncidents.length;
  const totalDeaths = filteredIncidents.reduce((sum, inc) => sum + inc.numDeaths, 0);
  const totalInjuries = filteredIncidents.reduce((sum, inc) => sum + inc.numInjuries, 0);
  const criticalIncidents = filteredIncidents.filter(i => i.severity === 'Critical').length;

  // By State
  const stateData = useMemo(() => {
    const counts: Record<string, number> = {};
    filteredIncidents.forEach(inc => {
      const name = getStateName(inc.stateId);
      counts[name] = (counts[name] || 0) + 1;
    });
    return Object.entries(counts).map(([name, count]) => ({
      name,
      count
    })).sort((a, b) => b.count - a.count);
  }, [filteredIncidents]);

  // By Type
  const typeData = useMemo(() => {
    const counts: Record<string, number> = {};
    filteredIncidents.forEach(inc => {
      const name = initialCaseTypes.find(t => t.id === inc.caseTypeId)?.name || inc.caseTypeId;
      counts[name] = (counts[name] || 0) + 1;
    });
    return Object.entries(counts).map(([name, count]) => ({
      name,
      count
    })).sort((a, b) => b.count - a.count);
  }, [filteredIncidents]);

  // Periodic (Monthly)
  const periodicData = useMemo(() => {
    const counts: Record<string, number> = {};
    filteredIncidents.forEach(inc => {
      const month = inc.date.substring(0, 7); // YYYY-MM
      counts[month] = (counts[month] || 0) + 1;
    });
    return Object.entries(counts).map(([month, count]) => ({
      month,
      count
    })).sort((a, b) => a.month.localeCompare(b.month));
  }, [filteredIncidents]);

  // Casualty by Type
  const casualtyData = useMemo(() => {
    const data: Record<string, {
      deaths: number;
      injuries: number;
    }> = {};
    filteredIncidents.forEach(inc => {
      const name = initialCaseTypes.find(t => t.id === inc.caseTypeId)?.name || inc.caseTypeId;
      if (!data[name]) data[name] = {
        deaths: 0,
        injuries: 0
      };
      data[name].deaths += inc.numDeaths;
      data[name].injuries += inc.numInjuries;
    });
    return Object.entries(data).map(([name, stats]) => ({
      name,
      ...stats
    })).sort((a, b) => b.deaths + b.injuries - (a.deaths + a.injuries));
  }, [filteredIncidents]);
  const renderTabNav = () => <div className="flex overflow-x-auto border-b border-gray-200 mb-6" data-id="element-534" data-mp="3go9xnlaya85l">
      <TabButton active={activeTab === 'National'} onClick={() => setActiveTab('National')} icon={<Activity size={16} data-id="element-536" data-mp="aw292edlmmoz" />} label="National Security" data-id="element-535" data-mp="3brjgsvrbn8j4" />
      <TabButton active={activeTab === 'Geographic'} onClick={() => setActiveTab('Geographic')} icon={<Map size={16} data-id="element-538" data-mp="fwq0v29nj7k9" />} label="State/Regional" data-id="element-537" data-mp="axn8ahauytt6" />
      <TabButton active={activeTab === 'IncidentType'} onClick={() => setActiveTab('IncidentType')} icon={<FileText size={16} data-id="element-540" data-mp="pt34arv00zs7" />} label="Incident Types" data-id="element-539" data-mp="ktgqag7cjfic" />
      <TabButton active={activeTab === 'Casualty'} onClick={() => setActiveTab('Casualty')} icon={<AlertTriangle size={16} data-id="element-542" data-mp="tsby1wq1gxnr" />} label="Casualty/Impact" data-id="element-541" data-mp="pqqfpvuqjr66" />
      <TabButton active={activeTab === 'Periodic'} onClick={() => setActiveTab('Periodic')} icon={<Calendar size={16} data-id="element-544" data-mp="13jy65k72ygma" />} label="Periodic Trends" data-id="element-543" data-mp="ysg40stbm9zq" />
    </div>;
  return <div className="space-y-6 max-w-7xl mx-auto pb-12" data-id="element-545" data-mp="18j18gwvoe8qk">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-546" data-mp="18ij41cyv0o7k">
        <div data-id="element-547" data-mp="1djyszg45rev8">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-548" data-mp="1dgaxwcj2ekee">Reports & Analytics</h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-549" data-mp="1hkim88lm1yc3">Comprehensive analysis of national security incidents</p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-blue-800 text-white text-sm font-medium rounded-md hover:bg-blue-900 transition-colors" data-id="element-550" data-mp="31zxbcnydpjum">
          <Download size={16} data-id="element-551" data-mp="2t2ei4wakzz7n" />
          Export Report PDF
        </button>
      </div>

      {/* Global Filters */}
      <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm" data-id="element-552" data-mp="2o3thivrf3dio">
        <div className="flex items-center gap-2 mb-4 text-sm font-medium text-gray-800" data-id="element-553" data-mp="3lozbg2amaldu">
          <Filter size={16} className="text-blue-600" data-id="element-554" data-mp="3gqo17qgeu8v3" />
          Report Filters
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4" data-id="element-555" data-mp="3bzfr0s79ajgv">
          <div data-id="element-556" data-mp="371e7k65x4i4v">
            <label className="block text-xs text-gray-500 mb-1" data-id="element-557" data-mp="371wdjuiduapz">Start Date</label>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-full border border-gray-300 rounded-md text-sm py-1.5 px-2 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-558" data-mp="8aukpbm2wxe1" />
          </div>
          <div data-id="element-559" data-mp="1xtjbwm96dxdr">
            <label className="block text-xs text-gray-500 mb-1" data-id="element-560" data-mp="1xsrohypqf5yk">End Date</label>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-full border border-gray-300 rounded-md text-sm py-1.5 px-2 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-561" data-mp="21ss7lwns05by" />
          </div>
          <div data-id="element-562" data-mp="1szf4k1vzznc5">
            <label className="block text-xs text-gray-500 mb-1" data-id="element-563" data-mp="1e48wl9dcdeg6">State</label>
            <select value={selectedState} onChange={e => setSelectedState(e.target.value)} className="w-full border border-gray-300 rounded-md text-sm py-1.5 px-2 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-564" data-mp="1e2xwbjj3uott">
              <option value="" data-id="element-565" data-mp="155x9viflj0t0">All States</option>
              {states.map(s => <option key={s.id} value={s.id} data-id="element-566" data-mp="1a375jftkw8pa">{s.name}</option>)}
            </select>
          </div>
          <div data-id="element-567" data-mp="2zi4xsbti2hfa">
            <label className="block text-xs text-gray-500 mb-1" data-id="element-568" data-mp="2zfiq8c6m01x2">Incident Type</label>
            <select value={selectedType} onChange={e => setSelectedType(e.target.value)} className="w-full border border-gray-300 rounded-md text-sm py-1.5 px-2 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-569" data-mp="34fx1s47f5tzu">
              <option value="" data-id="element-570" data-mp="39jfq3ue014ph">All Types</option>
              {initialCaseTypes.map(t => <option key={t.id} value={t.id} data-id="element-571" data-mp="3nei0atpvpg83">{t.name}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6" data-id="element-572" data-mp="3ikdtqbkiza7j">
        {renderTabNav()}

        {activeTab === 'National' && <div className="space-y-8" data-id="element-573" data-mp="3sayp7q4wjym8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-574" data-mp="3se4f37lcv4j0">
              <SummaryCard title="Total Approved Incidents" value={totalIncidents} color="blue" data-id="element-575" data-mp="a53vxfno4r5k" />
              <SummaryCard title="Total Deaths" value={totalDeaths} color="red" data-id="element-576" data-mp="65v08592ij7d" />
              <SummaryCard title="Total Injuries" value={totalInjuries} color="orange" data-id="element-577" data-mp="2bx06on20hwza" />
              <SummaryCard title="Critical Incidents" value={criticalIncidents} color="red" data-id="element-578" data-mp="27womfbdk9yoc" />
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-579" data-mp="2wg22q3lbjdx9">
              <div className="border border-gray-200 rounded-lg p-4" data-id="element-580" data-mp="31fy5y0m6z1br">
                <h3 className="text-lg font-medium mb-4" data-id="element-581" data-mp="31e3l0max797o">Incidents by State</h3>
                <div className="h-80" data-id="element-582" data-mp="367pxoeh33d3h">
                  <ResponsiveContainer width="100%" height="100%" data-id="element-583" data-mp="3a89ufgxqwep3">
                    <BarChart data={stateData} layout="vertical" margin={{
                  top: 5,
                  right: 30,
                  left: 40,
                  bottom: 5
                }} data-id="element-584" data-mp="3aam99l87i8xx">
                      <CartesianGrid strokeDasharray="3 3" data-id="element-585" data-mp="2ihc2wg747057" />
                      <XAxis type="number" data-id="element-586" data-mp="2ihvl7y0ofqjr" />
                      <YAxis dataKey="name" type="category" width={100} tick={{
                    fontSize: 12
                  }} data-id="element-587" data-mp="2mixe7x4036da" />
                      <Tooltip data-id="element-588" data-mp="2ri0ivgg2jakq" />
                      <Bar dataKey="count" fill="#3b82f6" radius={[0, 4, 4, 0]} data-id="element-589" data-mp="2ria6ixdn55n9" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="border border-gray-200 rounded-lg p-4" data-id="element-590" data-mp="6r0115tqul1z">
                <h3 className="text-lg font-medium mb-4" data-id="element-591" data-mp="gvq5pjgj839w">Incident Types Distribution</h3>
                <div className="h-80" data-id="element-592" data-mp="gxki29dqmn2d">
                  <ResponsiveContainer width="100%" height="100%" data-id="element-593" data-mp="bwdaaablhnvp">
                    <PieChart data-id="element-594" data-mp="75ww2nzy9y8a">
                      <Pie data={typeData} cx="50%" cy="50%" outerRadius={100} fill="#8884d8" dataKey="count" label={({
                    name,
                    percent
                  }) => `${name.split(' ')[0]} ${(percent * 100).toFixed(0)}%`} data-id="element-595" data-mp="754y56ivroeu">
                        {typeData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} data-id="element-596" data-mp="3u89rdql4qpxl" data-mpi={index} />)}
                      </Pie>
                      <Tooltip data-id="element-597" data-mp="3kdht0kjq3z1r" />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>}

        {activeTab === 'Geographic' && <div className="space-y-8" data-id="element-598" data-mp="1ikjxui2h4udn">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-599" data-mp="1eji2icboh2gy">State & Regional Reports</h2>
            <div className="h-96 border border-gray-200 rounded-lg p-4" data-id="element-600" data-mp="1lzixgujx7wk5">
               <ResponsiveContainer width="100%" height="100%" data-id="element-601" data-mp="1lxpr7gd7zeco">
                  <BarChart data={stateData} margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 5
            }} data-id="element-602" data-mp="1hww24jo1jti7">
                    <CartesianGrid strokeDasharray="3 3" data-id="element-603" data-mp="1vwnmd255i64n" />
                    <XAxis dataKey="name" data-id="element-604" data-mp="1qym0yak95uq0" />
                    <YAxis data-id="element-605" data-mp="1qzx6aetcjaj8" />
                    <Tooltip data-id="element-606" data-mp="1qwj3j5grszb9" />
                    <Legend data-id="element-607" data-mp="138rr81v8cm9z" />
                    <Bar dataKey="count" name="Incidents" fill="#0ea5e9" data-id="element-608" data-mp="13a2tfx911lyz" />
                  </BarChart>
                </ResponsiveContainer>
            </div>
            <div className="overflow-x-auto border border-gray-200 rounded-lg" data-id="element-609" data-mp="1cyuhvvevpo55">
              <table className="w-full text-left border-collapse" data-id="element-610" data-mp="1d0xh961x8tmj">
                <thead data-id="element-611" data-mp="187cg55517jj4">
                  <tr className="bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase" data-id="element-612" data-mp="186taamsnollg">
                    <th className="px-6 py-3" data-id="element-613" data-mp="kg728vinj1bg">State</th>
                    <th className="px-6 py-3 text-right" data-id="element-614" data-mp="finn7te4qkig">Total Incidents</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200" data-id="element-615" data-mp="2r5hg9ipgla53">
                  {stateData.map(s => <tr key={s.name} className="hover:bg-gray-50" data-id="element-616" data-mp="2vzln81yq5i66">
                      <td className="px-6 py-3 text-sm font-medium text-gray-900" data-id="element-617" data-mp="2i7f7ggzhhd79">{s.name}</td>
                      <td className="px-6 py-3 text-sm text-right" data-id="element-618" data-mp="2m8qzsu9qscj5">{s.count}</td>
                    </tr>)}
                  {stateData.length === 0 && <tr data-id="element-619" data-mp="3ewwlpkcj87kk">
                      <td colSpan={2} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-620" data-mp="3evbo1lux88ih">No data available for the selected filters.</td>
                    </tr>}
                </tbody>
              </table>
            </div>
          </div>}

        {activeTab === 'IncidentType' && <div className="space-y-8" data-id="element-621" data-mp="1pfvkclzrnezz">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-622" data-mp="1ugjl3zdsra7e">Incident-Type Reports</h2>
            <div className="h-96 border border-gray-200 rounded-lg p-4" data-id="element-623" data-mp="1ue6ukxtz3riq">
               <ResponsiveContainer width="100%" height="100%" data-id="element-624" data-mp="3w4xhz7b8s0do">
                  <BarChart data={typeData} margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 50
            }} data-id="element-625" data-mp="3r6ck3ejgpsse">
                    <CartesianGrid strokeDasharray="3 3" data-id="element-626" data-mp="3meug0hm57c37" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} tick={{
                fontSize: 12
              }} data-id="element-627" data-mp="3ieajng9e8ci4" />
                    <YAxis data-id="element-628" data-mp="3df7mehznnakp" />
                    <Tooltip data-id="element-629" data-mp="3dfhdya0clpsi" />
                    <Bar dataKey="count" name="Incidents" fill="#8b5cf6" data-id="element-630" data-mp="3dgiswpkhiwfi" />
                  </BarChart>
                </ResponsiveContainer>
            </div>
          </div>}

        {activeTab === 'Casualty' && <div className="space-y-8" data-id="element-631" data-mp="33iuwlv78dgv8">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-632" data-mp="2zg81nlf9jftt">Casualty & Impact Reports</h2>
            <div className="h-96 border border-gray-200 rounded-lg p-4" data-id="element-633" data-mp="11q6phargu9a6">
               <ResponsiveContainer width="100%" height="100%" data-id="element-634" data-mp="xo3cuit28ynb">
                  <BarChart data={casualtyData} margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 50
            }} data-id="element-635" data-mp="xouxiyl4dbyt">
                    <CartesianGrid strokeDasharray="3 3" data-id="element-636" data-mp="2flg6yagka54c" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} tick={{
                fontSize: 12
              }} data-id="element-637" data-mp="2kjzs48nbzcvn" />
                    <YAxis data-id="element-638" data-mp="2pckjz4moffwj" />
                    <Tooltip data-id="element-639" data-mp="2pcb449vcip4k" />
                    <Legend verticalAlign="top" data-id="element-640" data-mp="2td3dd347wu0u" />
                    <Bar dataKey="deaths" name="Deaths" fill="#ef4444" data-id="element-641" data-mp="2tbtfof1a1nuq" />
                    <Bar dataKey="injuries" name="Injuries" fill="#f97316" data-id="element-642" data-mp="1wnm0o46ti7pn" />
                  </BarChart>
                </ResponsiveContainer>
            </div>
            <div className="overflow-x-auto border border-gray-200 rounded-lg" data-id="element-643" data-mp="21o1f6z2u5l97">
              <table className="w-full text-left border-collapse" data-id="element-644" data-mp="25m7fbo9652l5">
                <thead data-id="element-645" data-mp="2amve4w3n2npx">
                  <tr className="bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase" data-id="element-646" data-mp="2ao6iax0zxxhk">
                    <th className="px-6 py-3" data-id="element-647" data-mp="3h4rzg1qg4m9c">Incident Type</th>
                    <th className="px-6 py-3 text-right" data-id="element-648" data-mp="3m0qs41it4qfk">Deaths</th>
                    <th className="px-6 py-3 text-right" data-id="element-649" data-mp="3kkd5johcsbiy">Injuries</th>
                    <th className="px-6 py-3 text-right" data-id="element-650" data-mp="3kmpkdsrte5rs">Total Casualties</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200" data-id="element-651" data-mp="3tl1iefv0g5u9">
                  {casualtyData.map(c => <tr key={c.name} className="hover:bg-gray-50" data-id="element-652" data-mp="3pj6hvdkwlwhj">
                      <td className="px-6 py-3 text-sm font-medium text-gray-900" data-id="element-653" data-mp="3pm2ft1wr4l5z">{c.name}</td>
                      <td className="px-6 py-3 text-sm text-right text-red-600 font-medium" data-id="element-654" data-mp="7c07k1l7rkrt">{c.deaths}</td>
                      <td className="px-6 py-3 text-sm text-right text-orange-600 font-medium" data-id="element-655" data-mp="2d5qb42wa49t">{c.injuries}</td>
                      <td className="px-6 py-3 text-sm text-right font-bold text-gray-900" data-id="element-656" data-mp="c4tb4m4y4n2b">{c.deaths + c.injuries}</td>
                    </tr>)}
                  {casualtyData.length === 0 && <tr data-id="element-657" data-mp="l5zwp9mgco2f">
                      <td colSpan={4} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-658" data-mp="l6sv9nb9c79h">No data available for the selected filters.</td>
                    </tr>}
                </tbody>
              </table>
            </div>
          </div>}

        {activeTab === 'Periodic' && <div className="space-y-8" data-id="element-659" data-mp="vq1xqrpj2i2n">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-660" data-mp="zs6ptm0bv6n6">Monthly & Periodic Reports</h2>
            <div className="h-96 border border-gray-200 rounded-lg p-4" data-id="element-661" data-mp="zu196vvyaqp5">
               <ResponsiveContainer width="100%" height="100%" data-id="element-662" data-mp="lzh3thksu1q2">
                  <LineChart data={periodicData} margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 5
            }} data-id="element-663" data-mp="qsjy5rxehf7b">
                    <CartesianGrid strokeDasharray="3 3" data-id="element-664" data-mp="c3ngr49gepu1" />
                    <XAxis dataKey="month" data-id="element-665" data-mp="h1p1dtmj2x7m" />
                    <YAxis data-id="element-666" data-mp="h2gvt14e2owb" />
                    <Tooltip data-id="element-667" data-mp="gzmc5bt7983y" />
                    <Legend data-id="element-668" data-mp="3265bz6zrcbo" />
                    <Line type="monotone" dataKey="count" name="Incidents" stroke="#0ea5e9" strokeWidth={3} activeDot={{
                r: 8
              }} data-id="element-669" data-mp="32z34anz2rho" />
                  </LineChart>
                </ResponsiveContainer>
            </div>
            <div className="overflow-x-auto border border-gray-200 rounded-lg" data-id="element-670" data-mp="29qya8p61fcfz">
              <table className="w-full text-left border-collapse" data-id="element-671" data-mp="24yf0626k6zfx">
                <thead data-id="element-672" data-mp="2003pxqccqmx6">
                  <tr className="bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase" data-id="element-673" data-mp="1zzln2av6paga">
                    <th className="px-6 py-3" data-id="element-674" data-mp="1w02y9408sf86">Month</th>
                    <th className="px-6 py-3 text-right" data-id="element-675" data-mp="2sosiv2397lxu">Total Incidents</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200" data-id="element-676" data-mp="2nppp4dsq5256">
                  {periodicData.map(p => <tr key={p.month} className="hover:bg-gray-50" data-id="element-677" data-mp="2jnb5hrh8dycu">
                      <td className="px-6 py-3 text-sm font-medium text-gray-900" data-id="element-678" data-mp="2epjd6wcoca84">{p.month}</td>
                      <td className="px-6 py-3 text-sm text-right" data-id="element-679" data-mp="188xl4wlos11k">{p.count}</td>
                    </tr>)}
                  {periodicData.length === 0 && <tr data-id="element-680" data-mp="13c7343qbmvdi">
                      <td colSpan={2} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-681" data-mp="3expv40zlarcq">No data available for the selected filters.</td>
                    </tr>}
                </tbody>
              </table>
            </div>
          </div>}

      </div>
    </div>;
}
function TabButton({
  active,
  onClick,
  icon,
  label
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return <button onClick={onClick} className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${active ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-682" data-mp="1i1mjf9qyk91">
      {icon}
      {label}
    </button>;
}
function SummaryCard({
  title,
  value,
  color
}: {
  title: string;
  value: number;
  color: 'blue' | 'red' | 'orange' | 'green';
}) {
  const colors = {
    blue: 'bg-blue-50 text-blue-700 border-blue-100',
    red: 'bg-red-50 text-red-700 border-red-100',
    orange: 'bg-orange-50 text-orange-700 border-orange-100',
    green: 'bg-green-50 text-green-700 border-green-100'
  };
  return <div className={`p-4 rounded-lg border ${colors[color]}`} data-id="element-683" data-mp="275r82183kg71">
      <h4 className="text-sm font-medium opacity-80 mb-1" data-id="element-684" data-mp="275940if70xok">{title}</h4>
      <p className="text-3xl font-bold" data-id="element-685" data-mp="233xai1t77sb3">{value}</p>
    </div>;
}